import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import Layout from '../../components/Layout';
import LoadingState from '../../components/LoadingState';
import { ArrowLeft, Layers, FolderOpen, Users as UsersIcon, GitBranch, Plus, Trash2, X, Loader2, Check, LayoutDashboard } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TABS = [
  { key: 'projects', label: 'Projects', icon: FolderOpen },
  { key: 'programs', label: 'Programs', icon: GitBranch },
  { key: 'members', label: 'Members', icon: UsersIcon },
];

const PortfolioDetail = () => {
  const { portfolioId } = useParams();
  const navigate = useNavigate();
  const [portfolio, setPortfolio] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('projects');

  const [projects, setProjects] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [members, setMembers] = useState([]);

  const [showAssign, setShowAssign] = useState(false);
  const [candidates, setCandidates] = useState([]);
  const [selected, setSelected] = useState({});
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);

  const [showProgram, setShowProgram] = useState(false);
  const [programForm, setProgramForm] = useState({ name: '', description: '' });

  const [showMember, setShowMember] = useState(false);
  const [users, setUsers] = useState([]);
  const [memberForm, setMemberForm] = useState({ userId: '', role: 'PORTFOLIO_VIEWER' });

  const loadCore = useCallback(async () => {
    try {
      const [p, proj, prog] = await Promise.all([
        axios.get(`${API}/portfolios/${portfolioId}`),
        axios.get(`${API}/portfolios/${portfolioId}/projects`),
        axios.get(`${API}/portfolios/${portfolioId}/programs`),
      ]);
      setPortfolio(p.data);
      setProjects(proj.data);
      setPrograms(prog.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Portfolio not found');
      navigate('/admin/portfolios');
    } finally {
      setLoading(false);
    }
  }, [portfolioId, navigate]);

  useEffect(() => { loadCore(); }, [loadCore]);

  const loadMembers = async () => {
    try { setMembers((await axios.get(`${API}/portfolios/${portfolioId}/members`)).data); }
    catch (e) { toast.error('Failed to load members'); }
  };
  useEffect(() => { if (tab === 'members') loadMembers(); /* eslint-disable-next-line */ }, [tab]);

  // ---------- assignment
  const openAssign = async () => {
    setSelected({}); setPreview(null);
    try {
      const res = await axios.get(`${API}/unassigned-projects`);
      setCandidates(res.data);
      setShowAssign(true);
    } catch (e) { toast.error('Failed to load projects'); }
  };

  const runPreview = async () => {
    const ids = Object.keys(selected).filter((k) => selected[k]);
    if (!ids.length) { toast.error('Select at least one project'); return; }
    setBusy(true);
    try {
      const res = await axios.post(`${API}/portfolios/${portfolioId}/projects/assign-preview`, { projectIds: ids });
      setPreview(res.data);
    } catch (e) { toast.error('Preview failed'); } finally { setBusy(false); }
  };

  const confirmAssign = async () => {
    const ids = preview.items.filter((i) => i.found).map((i) => i.projectId);
    setBusy(true);
    try {
      const res = await axios.post(`${API}/portfolios/${portfolioId}/projects/assign`, { projectIds: ids });
      toast.success(`Assigned ${res.data.assignedCount} project(s)`);
      setShowAssign(false); setPreview(null);
      loadCore();
    } catch (e) { toast.error('Assignment failed'); } finally { setBusy(false); }
  };

  const unassign = async (pid) => {
    if (!window.confirm('Remove this project from the portfolio?')) return;
    try {
      await axios.delete(`${API}/portfolios/${portfolioId}/projects/${pid}`);
      toast.success('Project removed');
      loadCore();
    } catch (e) { toast.error('Failed to remove'); }
  };

  // ---------- programs
  const createProgram = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/portfolios/${portfolioId}/programs`, programForm);
      toast.success('Program created');
      setShowProgram(false); setProgramForm({ name: '', description: '' });
      setPrograms((await axios.get(`${API}/portfolios/${portfolioId}/programs`)).data);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };
  const deleteProgram = async (id) => {
    if (!window.confirm('Delete program? Projects stay in the portfolio.')) return;
    try {
      await axios.delete(`${API}/portfolios/${portfolioId}/programs/${id}`);
      toast.success('Program deleted');
      setPrograms((await axios.get(`${API}/portfolios/${portfolioId}/programs`)).data);
    } catch (e) { toast.error('Failed'); }
  };

  // ---------- members
  const openMember = async () => {
    try { setUsers((await axios.get(`${API}/admin/users`)).data); setShowMember(true); }
    catch (e) { toast.error('Failed to load users'); }
  };
  const addMember = async (e) => {
    e.preventDefault();
    if (!memberForm.userId) { toast.error('Pick a user'); return; }
    try {
      await axios.post(`${API}/portfolios/${portfolioId}/members`, memberForm);
      toast.success('Member added');
      setShowMember(false); setMemberForm({ userId: '', role: 'PORTFOLIO_VIEWER' });
      loadMembers();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };
  const removeMember = async (uid) => {
    if (!window.confirm('Remove member access?')) return;
    try { await axios.delete(`${API}/portfolios/${portfolioId}/members/${uid}`); toast.success('Removed'); loadMembers(); }
    catch (e) { toast.error('Failed'); }
  };

  if (loading) return <Layout><LoadingState message="Loading portfolio..." /></Layout>;

  return (
    <Layout>
      <div className="space-y-6" data-testid="portfolio-detail-page">
        <button onClick={() => navigate('/admin/portfolios')} className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-400 hover:text-slate-900" data-testid="back-to-portfolios"><ArrowLeft className="w-4 h-4" />Portfolios</button>
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-indigo-100 dark:bg-indigo-900/40 rounded-lg flex items-center justify-center"><Layers className="w-6 h-6 text-indigo-600 dark:text-indigo-300" /></div>
            <div>
              <h1 className="text-2xl font-bold text-slate-900 dark:text-white flex items-center gap-2">{portfolio.name}<span className="text-xs font-mono text-slate-400">{portfolio.code}</span></h1>
              <p className="text-slate-600 dark:text-slate-400 text-sm">{portfolio.description || 'No description'}</p>
            </div>
          </div>
          <button onClick={() => navigate(`/app/portfolio/${portfolioId}/command`)} className="flex items-center gap-2 bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700 shrink-0" data-testid="open-command-center">
            <LayoutDashboard className="w-4 h-4" />Command Center
          </button>
        </div>

        <div className="flex gap-1 border-b border-slate-200 dark:border-slate-800">
          {TABS.map((t) => (
            <button key={t.key} onClick={() => setTab(t.key)} className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${tab === t.key ? 'border-blue-600 text-blue-600' : 'border-transparent text-slate-500 hover:text-slate-800'}`} data-testid={`tab-${t.key}`}>
              <t.icon className="w-4 h-4" />{t.label}
            </button>
          ))}
        </div>

        {/* PROJECTS */}
        {tab === 'projects' && (
          <div className="space-y-4" data-testid="projects-tab">
            <div className="flex justify-end">
              <button onClick={openAssign} className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700" data-testid="assign-projects-button"><Plus className="w-4 h-4" />Assign Projects</button>
            </div>
            {projects.length === 0 ? (
              <p className="text-slate-500 text-sm py-8 text-center">No projects assigned yet.</p>
            ) : (
              <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 divide-y divide-slate-100 dark:divide-slate-800">
                {projects.map((p) => (
                  <div key={p.id} className="flex items-center justify-between px-5 py-3" data-testid="portfolio-project-row">
                    <div className="flex items-center gap-3">
                      <FolderOpen className="w-5 h-5 text-slate-400" />
                      <div><p className="font-medium text-slate-900 dark:text-white">{p.name}</p><p className="text-xs text-slate-500 capitalize">{p.status}{p.location ? ` · ${p.location}` : ''}</p></div>
                    </div>
                    <button onClick={() => unassign(p.id)} className="p-2 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg" data-testid="unassign-project-button"><Trash2 className="w-4 h-4 text-red-600" /></button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* PROGRAMS */}
        {tab === 'programs' && (
          <div className="space-y-4" data-testid="programs-tab">
            <div className="flex justify-end">
              <button onClick={() => setShowProgram(true)} className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700" data-testid="create-program-button"><Plus className="w-4 h-4" />New Program</button>
            </div>
            {programs.length === 0 ? (
              <p className="text-slate-500 text-sm py-8 text-center">No programs. Programs are optional groupings within a portfolio.</p>
            ) : (
              <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 divide-y divide-slate-100 dark:divide-slate-800">
                {programs.map((pg) => (
                  <div key={pg.id} className="flex items-center justify-between px-5 py-3" data-testid="program-row">
                    <div className="flex items-center gap-3">
                      <GitBranch className="w-5 h-5 text-slate-400" />
                      <div><p className="font-medium text-slate-900 dark:text-white flex items-center gap-2">{pg.name}<span className="text-xs font-mono text-slate-400">{pg.code}</span></p><p className="text-xs text-slate-500">{pg.description || '—'}</p></div>
                    </div>
                    <button onClick={() => deleteProgram(pg.id)} className="p-2 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg" data-testid="delete-program-button"><Trash2 className="w-4 h-4 text-red-600" /></button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* MEMBERS */}
        {tab === 'members' && (
          <div className="space-y-4" data-testid="members-tab">
            <div className="flex justify-end">
              <button onClick={openMember} className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700" data-testid="add-member-button"><Plus className="w-4 h-4" />Add Member</button>
            </div>
            {members.length === 0 ? (
              <p className="text-slate-500 text-sm py-8 text-center">No explicit members. Company admins always have full access.</p>
            ) : (
              <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 divide-y divide-slate-100 dark:divide-slate-800">
                {members.map((m) => (
                  <div key={m.userId} className="flex items-center justify-between px-5 py-3" data-testid="member-row">
                    <div className="flex items-center gap-3">
                      <UsersIcon className="w-5 h-5 text-slate-400" />
                      <div><p className="font-medium text-slate-900 dark:text-white">{m.userName || m.userEmail || m.userId}</p><p className="text-xs text-slate-500">{m.userEmail}</p></div>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className={`text-xs px-2.5 py-1 rounded-full ${m.role === 'PORTFOLIO_MANAGER' ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300' : 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300'}`}>{m.role === 'PORTFOLIO_MANAGER' ? 'Manager' : 'Viewer'}</span>
                      <button onClick={() => removeMember(m.userId)} className="p-2 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg" data-testid="remove-member-button"><Trash2 className="w-4 h-4 text-red-600" /></button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* ASSIGN MODAL with preview */}
      {showAssign && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50" data-testid="assign-modal">
          <div className="bg-white dark:bg-slate-900 rounded-xl p-6 max-w-lg w-full shadow-2xl max-h-[85vh] flex flex-col">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-slate-900 dark:text-white">{preview ? 'Confirm Assignment' : 'Assign Projects'}</h2>
              <button onClick={() => setShowAssign(false)} data-testid="close-assign-modal"><X className="w-5 h-5 text-slate-400" /></button>
            </div>

            {!preview ? (
              <>
                <p className="text-sm text-slate-500 mb-3">Select unassigned projects to add to this portfolio.</p>
                <div className="flex-1 overflow-y-auto space-y-1 border border-slate-200 dark:border-slate-800 rounded-lg p-2">
                  {candidates.length === 0 ? <p className="text-sm text-slate-400 p-4 text-center">No unassigned projects available.</p> :
                    candidates.map((c) => (
                      <label key={c.id} className="flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800 cursor-pointer" data-testid="assign-candidate">
                        <input type="checkbox" checked={!!selected[c.id]} onChange={(e) => setSelected({ ...selected, [c.id]: e.target.checked })} className="w-4 h-4" data-testid={`candidate-checkbox-${c.id}`} />
                        <span className="text-sm text-slate-800 dark:text-slate-200">{c.name}</span>
                      </label>
                    ))}
                </div>
                <div className="flex gap-3 pt-4">
                  <button onClick={() => setShowAssign(false)} className="flex-1 px-4 py-2 bg-slate-100 dark:bg-slate-800 rounded-lg">Cancel</button>
                  <button onClick={runPreview} disabled={busy} className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg disabled:opacity-50 flex items-center justify-center gap-2" data-testid="preview-assign-button">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Preview'}</button>
                </div>
              </>
            ) : (
              <>
                <p className="text-sm text-slate-500 mb-3">Review before confirming. Reassignments are flagged.</p>
                <div className="flex-1 overflow-y-auto space-y-2 border border-slate-200 dark:border-slate-800 rounded-lg p-3">
                  {preview.items.map((i) => (
                    <div key={i.projectId} className="flex items-center justify-between text-sm" data-testid="preview-item">
                      <span className="text-slate-800 dark:text-slate-200">{i.name || i.projectId}</span>
                      {!i.found ? <span className="text-red-500 text-xs">not found</span>
                        : i.willReassign ? <span className="text-amber-600 text-xs">move from {i.currentPortfolioName || 'other'}</span>
                        : <span className="text-emerald-600 text-xs flex items-center gap-1"><Check className="w-3 h-3" />new</span>}
                    </div>
                  ))}
                </div>
                <div className="flex gap-3 pt-4">
                  <button onClick={() => setPreview(null)} className="flex-1 px-4 py-2 bg-slate-100 dark:bg-slate-800 rounded-lg">Back</button>
                  <button onClick={confirmAssign} disabled={busy} className="flex-1 px-4 py-2 bg-emerald-600 text-white rounded-lg disabled:opacity-50 flex items-center justify-center gap-2" data-testid="confirm-assign-button">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Confirm Assignment'}</button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* PROGRAM MODAL */}
      {showProgram && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50" data-testid="program-modal">
          <div className="bg-white dark:bg-slate-900 rounded-xl p-6 max-w-md w-full shadow-2xl">
            <h2 className="text-xl font-bold text-slate-900 dark:text-white mb-4">New Program</h2>
            <form onSubmit={createProgram} className="space-y-4">
              <input type="text" placeholder="Program name" value={programForm.name} onChange={(e) => setProgramForm({ ...programForm, name: e.target.value })} required className="w-full px-4 py-2 bg-slate-50 dark:bg-slate-800 border border-slate-300 dark:border-slate-700 rounded-lg outline-none focus:ring-2 focus:ring-blue-500" data-testid="program-name-input" />
              <textarea placeholder="Description" value={programForm.description} onChange={(e) => setProgramForm({ ...programForm, description: e.target.value })} rows={3} className="w-full px-4 py-2 bg-slate-50 dark:bg-slate-800 border border-slate-300 dark:border-slate-700 rounded-lg outline-none focus:ring-2 focus:ring-blue-500" data-testid="program-description-input" />
              <div className="flex gap-3">
                <button type="button" onClick={() => setShowProgram(false)} className="flex-1 px-4 py-2 bg-slate-100 dark:bg-slate-800 rounded-lg">Cancel</button>
                <button type="submit" className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg" data-testid="submit-program-button">Create</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* MEMBER MODAL */}
      {showMember && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50" data-testid="member-modal">
          <div className="bg-white dark:bg-slate-900 rounded-xl p-6 max-w-md w-full shadow-2xl">
            <h2 className="text-xl font-bold text-slate-900 dark:text-white mb-4">Add Member</h2>
            <form onSubmit={addMember} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">User</label>
                <select value={memberForm.userId} onChange={(e) => setMemberForm({ ...memberForm, userId: e.target.value })} className="w-full px-4 py-2 bg-slate-50 dark:bg-slate-800 border border-slate-300 dark:border-slate-700 rounded-lg outline-none focus:ring-2 focus:ring-blue-500" data-testid="member-user-select">
                  <option value="">Select a user...</option>
                  {users.map((u) => <option key={u.id} value={u.id}>{u.name || u.email}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">Role</label>
                <select value={memberForm.role} onChange={(e) => setMemberForm({ ...memberForm, role: e.target.value })} className="w-full px-4 py-2 bg-slate-50 dark:bg-slate-800 border border-slate-300 dark:border-slate-700 rounded-lg outline-none focus:ring-2 focus:ring-blue-500" data-testid="member-role-select">
                  <option value="PORTFOLIO_VIEWER">Viewer (read portfolio)</option>
                  <option value="PORTFOLIO_MANAGER">Manager (manage portfolio)</option>
                </select>
              </div>
              <div className="flex gap-3">
                <button type="button" onClick={() => setShowMember(false)} className="flex-1 px-4 py-2 bg-slate-100 dark:bg-slate-800 rounded-lg">Cancel</button>
                <button type="submit" className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg" data-testid="submit-member-button">Add</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default PortfolioDetail;
